import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';
import { endSession, isSessionEnding } from '@/lib/session';

const ACTIVITY_STORAGE_KEY = 'last_activity_at';
const WARNING_TOAST_ID = 'idle-timeout-warning';

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

const DEFAULT_IDLE_MINUTES = 30;
const CHECK_INTERVAL_MS = 5_000;
const ACTIVITY_WRITE_THROTTLE_MS = 1_000;
const WARNING_LEAD_MS = 60_000;

export const resolveIdleTimeoutMs = (): number => {
  const configured = Number(import.meta.env.VITE_IDLE_TIMEOUT_MINUTES);
  const minutes = Number.isFinite(configured) && configured > 0 ? configured : DEFAULT_IDLE_MINUTES;
  return minutes * 60_000;
};

export const useIdleTimeout = (enabled: boolean): void => {
  const warnedRef = useRef(false);
  const lastWriteRef = useRef(0);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const idleTimeoutMs = resolveIdleTimeoutMs();
    const warnAfterMs = Math.max(idleTimeoutMs - WARNING_LEAD_MS, Math.floor(idleTimeoutMs / 2));

    const readLastActivity = (): number => {
      const stored = Number(localStorage.getItem(ACTIVITY_STORAGE_KEY));
      return Number.isFinite(stored) && stored > 0 ? stored : Date.now();
    };

    const markActive = (): void => {
      if (warnedRef.current) {
        toast.dismiss(WARNING_TOAST_ID);
        warnedRef.current = false;
      }

      const now = Date.now();
      if (now - lastWriteRef.current < ACTIVITY_WRITE_THROTTLE_MS) {
        return;
      }
      lastWriteRef.current = now;
      localStorage.setItem(ACTIVITY_STORAGE_KEY, String(now));
    };

    const checkIdle = (): void => {
      if (isSessionEnding() || !localStorage.getItem('auth_token')) {
        return;
      }

      const idleForMs = Date.now() - readLastActivity();

      if (idleForMs >= idleTimeoutMs) {
        logger.info('Signing out after inactivity', {
          idleMinutes: Math.round(idleForMs / 60_000),
        });
        toast.dismiss(WARNING_TOAST_ID);
        endSession('inactivity');
        return;
      }

      if (idleForMs >= warnAfterMs && !warnedRef.current) {
        warnedRef.current = true;
        const secondsLeft = Math.max(1, Math.round((idleTimeoutMs - idleForMs) / 1000));
        toast.warning(`Signing you out in ${secondsLeft}s due to inactivity.`, {
          id: WARNING_TOAST_ID,
          duration: Infinity,
          action: { label: 'Stay signed in', onClick: markActive },
        });
      }
    };

    markActive();

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, markActive, { passive: true }),
    );
    document.addEventListener('visibilitychange', checkIdle);
    const interval = setInterval(checkIdle, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, markActive));
      document.removeEventListener('visibilitychange', checkIdle);
      clearInterval(interval);
      toast.dismiss(WARNING_TOAST_ID);
    };
  }, [enabled]);
};
